/* ---- Event detail info panel ---- */

import { ICONS } from '../icons';
import type { EventDetail } from '../types';
import { escapeHtml } from '../utils/html';

function typeLabel(type: string): string {
  switch (type) {
    case 'zone_entry':
      return '入場';
    case 'zone_stay':
      return '滞在';
    case 'zone_exit':
      return '退場';
    default:
      return type;
  }
}

function typeBadgeClass(type: string): string {
  switch (type) {
    case 'zone_entry':
      return 'be';
    case 'zone_stay':
      return 'bs';
    case 'zone_exit':
      return 'bx';
    default:
      return 'be';
  }
}

function formatDate(dateStr: string): string {
  const d = new Date(dateStr);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  const h = String(d.getHours()).padStart(2, '0');
  const min = String(d.getMinutes()).padStart(2, '0');
  return `${y}-${m}-${day} ${h}:${min}`;
}

function shortenHash(hash: string): string {
  if (!hash) return '—';
  if (hash.length <= 20) return hash;
  return `${hash.slice(0, 8)}…${hash.slice(-8)}`;
}

/** Map overlay with geofence name and badge */
export function renderDetailOverlay(event: EventDetail): HTMLElement {
  const el = document.createElement('div');
  el.className = 'fo';
  el.innerHTML = `
    <div class="fn">${ICONS.fence}<span>${escapeHtml(event.fence)}</span></div>
    <span class="bg ${typeBadgeClass(event.type)}">${typeLabel(event.type)}</span>
  `;
  return el;
}

/** Bottom info panel for event detail */
export function renderEventDetailInfo(event: EventDetail, onVcIssue: () => void): HTMLElement {
  const el = document.createElement('div');
  el.className = 'ip';

  const isStay = event.type === 'zone_stay';

  let dateHtml: string;
  if (isStay && event.date_from && event.date_to) {
    dateHtml = `
      <div class="dft">
        <div class="ln"><span class="lb">FROM</span><span class="vl2">${formatDate(event.date_from)}</span></div>
        <div class="ln"><span class="lb">TO</span><span class="vl2">${formatDate(event.date_to)}</span></div>
      </div>
    `;
  } else {
    dateHtml = `<div class="dd2">${formatDate(event.date)}</div>`;
  }

  const alt = event.alt?.toFixed(1) ?? '--';

  let vcHtml: string;
  if (event.verified) {
    vcHtml = `<button class="vib" id="vc-issue-btn">${ICONS.shieldBtn} 位置証明書を発行する ${ICONS.external}</button>`;
  } else {
    vcHtml = `<div class="vnb">${ICONS.cross}<span>VC未発行 \u2014 位置証明書が未発行です</span></div>`;
  }

  const startHash = event.start_hash;
  const endHash = event.end_hash;
  const hashHtml = `
    <div class="hb">
      <div class="hr" data-hash="${escapeHtml(startHash)}" title="${escapeHtml(startHash)}">
        <span class="hl">START HASH</span>
        <span class="hv">${escapeHtml(shortenHash(startHash))}</span>
        <span class="hc">${ICONS.copy}</span>
      </div>
      <div class="hr" data-hash="${escapeHtml(endHash)}" title="${escapeHtml(endHash)}">
        <span class="hl">END HASH</span>
        <span class="hv">${escapeHtml(shortenHash(endHash))}</span>
        <span class="hc">${ICONS.copy}</span>
      </div>
    </div>
  `;

  el.innerHTML = `
    <div class="il">${ICONS.calendar}<span>イベント情報</span></div>
    ${dateHtml}
    <div class="da">${escapeHtml(event.address || '')}</div>
    <div class="ic">
      <span>${event.lat}\u00B0 N, ${event.lng}\u00B0 E</span>
      <span>高度 <span class="av">${alt}</span> m</span>
    </div>
    ${hashHtml}
    ${vcHtml}
  `;

  el.querySelectorAll<HTMLElement>('.hr').forEach((row) => {
    row.addEventListener('click', async () => {
      const value = row.dataset.hash || '';
      if (!value) return;
      try {
        await navigator.clipboard.writeText(value);
      } catch {
        const ta = document.createElement('textarea');
        ta.value = value;
        ta.style.position = 'fixed';
        ta.style.opacity = '0';
        document.body.appendChild(ta);
        ta.select();
        try {
          document.execCommand('copy');
        } catch {
          /* noop */
        }
        document.body.removeChild(ta);
      }
      row.classList.add('copied');
      setTimeout(() => row.classList.remove('copied'), 1200);
    });
  });

  if (event.verified) {
    el.querySelector('#vc-issue-btn')!.addEventListener('click', onVcIssue);
  }

  return el;
}
